import React, { useCallback } from 'react';
import moment from 'moment';
import ModalLayout from '../../../../../shared/components/structure/ModalLayout';
import useCSVHandler from '../../../../../shared/hooks/UseCSVHandler';
import { getSparkBaseUrl } from '../../../../../shared/constants/Urls';
import { TransferDataRecord, sortDataRecords } from './dashboardTypes';
import { useTransferDataModalFilters } from '../hooks/useDataModalFilters';
import { TRANSFER_REASON_LABELS } from '../../../../../shared/components/charts/TransfersChart';
import {
    color_text_dashboard_muted,
    chart_planned_fill_light,
    chart_planned_solid,
    chart_unplanned_fill_light,
    chart_unplanned_solid,
    voicify_spark_light,
    voicify_spark_dark,
    voicify_spark_darker
} from '../../../../../shared/constants/colors';
import {
    CallDataModalContent,
    CallDataModalHeader,
    CallDataModalTitleSection,
    CallDataModalTitle,
    CallDataDateRange,
    CallDataModalCloseButton,
    CallDataScrollContainer,
    CallDataTable,
    CallDataTh,
    CallDataRow,
    CallDataTd,
    TranscriptLink,
    CallTag,
    CallDataModalFooter,
    ExportInfoText,
    ExportButton,
    ButtonSpinner,
    SearchInputWrapper,
    FilterSearchInput,
    ClearSearchButton,
    ClearFilterLink,
    FilterActionsContainer,
    ResultsInfo,
    FilterableColumnHeader,
    ColumnHeaderContent,
    ColumnHeaderText,
    ColumnFilterIcon,
    SortIcon,
    ColumnFilterDropdown,
    ColumnFilterHeader,
    ColumnFilterContent,
    ColumnFilterItem,
    ColumnFilterCheckbox,
    PhoneFilterSection,
    PhoneFilterModeToggle,
    PhoneFilterModeButton,
    PhoneFilterList,
    PhoneFilterTag,
    PhoneFilterTagRemove,
    SimpleFilterControlsContainer,
    SimpleFilterRow,
    TruncatedLocationText
} from './ApplicationDashboard.styles';

const filterIcon = require('../../../../../shared/content/images/spark/filter.svg');

export interface TransferDataModalProps {
    isVisible: boolean;
    onClose: () => void;
    title: string;
    startDate: string;
    endDate: string;
    records: TransferDataRecord[];
    /** Custom export filename prefix */
    exportFileName?: string;
    /** Add an ES badge for Spanish-only filter mode */
    spanishOnlyFilter?: boolean;
    /** Transfer reasons to exclude by default (e.g., IMMEDIATE_TRANSFER). Modal will pre-filter these out. */
    defaultExcludedReasons?: string[];
}

/**
 * TransferDataModal - A reusable modal component for displaying transfer data tables
 * 
 * Features:
 * - Search across all fields
 * - Filter by phone (include/exclude mode), location, transfer type, transfer reason
 * - Sort by date, time, phone, location, transfer type, transfer reason
 * - Pagination with 100 records per page
 * - Export to CSV
 */
const TransferDataModal: React.FC<TransferDataModalProps> = ({
    isVisible,
    onClose,
    title,
    startDate,
    endDate,
    records,
    exportFileName,
    spanishOnlyFilter = false,
    defaultExcludedReasons
}) => {
    const csvHandler = useCSVHandler();
    const [isExporting, setIsExporting] = React.useState(false);

    // Helper function to get reason label
    const getReasonLabel = useCallback((reason: string): string => {
        return TRANSFER_REASON_LABELS[reason as keyof typeof TRANSFER_REASON_LABELS] || reason;
    }, []);

    // Use the filter hook
    const filters = useTransferDataModalFilters(
        records,
        isVisible,
        getReasonLabel,
        defaultExcludedReasons
    );

    // Handle close with filter reset
    const handleClose = useCallback(() => {
        filters.clearAllFilters();
        onClose();
    }, [filters, onClose]);

    // Export to CSV
    const handleExportCSV = useCallback(() => {
        setIsExporting(true);
        setTimeout(() => {
            try {
                const headers = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Transfer Type', 'Transfer Reason'];

                // Compute filtered data for export
                let dataToExport = records;
                if (filters.hasActiveFilters) {
                    dataToExport = records.filter(transfer => {
                        const searchLower = filters.searchQuery.toLowerCase();
                        const matchesSearch = !filters.searchQuery ||
                            transfer.location.toLowerCase().includes(searchLower) ||
                            transfer.phoneLastFour.toLowerCase().includes(searchLower) ||
                            transfer.date.toLowerCase().includes(searchLower) ||
                            transfer.time.toLowerCase().includes(searchLower) ||
                            transfer.transferType.toLowerCase().includes(searchLower) ||
                            getReasonLabel(transfer.transferReason).toLowerCase().includes(searchLower);

                        let matchesPhone = true;
                        if (filters.selectedPhones.size > 0) {
                            matchesPhone = filters.phoneFilterMode === 'include'
                                ? filters.selectedPhones.has(transfer.phoneLastFour)
                                : !filters.selectedPhones.has(transfer.phoneLastFour);
                        }

                        const matchesLocation = filters.selectedLocations.size === 0 || filters.selectedLocations.has(transfer.location);
                        const matchesType = filters.selectedTransferType === 'all' || transfer.transferType === filters.selectedTransferType;
                        const matchesReason = filters.selectedTransferReasons.size === 0 || filters.selectedTransferReasons.has(transfer.transferReason);

                        return matchesSearch && matchesPhone && matchesLocation && matchesType && matchesReason;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, filters.sortConfig, true);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(transfer => ({
                    'Date': transfer.date,
                    'Time': transfer.time,
                    'Phone (Last 4)': `***${transfer.phoneLastFour}`,
                    'Location': transfer.location,
                    'Transcript Link': `${baseUrl}/v/apps/${transfer.applicationId}/analytics/sessions?sessionId=${transfer.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`,
                    'Transfer Type': transfer.transferType,
                    'Transfer Reason': getReasonLabel(transfer.transferReason) || 'Undetermined'
                }));

                const filterSuffix = filters.hasActiveFilters ? '-filtered' : '';
                const prefix = exportFileName || 'transfers';
                const filename = `${prefix}-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExporting(false);
            }
        }, 50);
    }, [records, filters, getReasonLabel, exportFileName, startDate, endDate, csvHandler]);

    return (
        <ModalLayout isVisible={isVisible} onClose={handleClose} zIndex={1000}>
            <CallDataModalContent>
                <CallDataModalHeader>
                    <CallDataModalTitleSection>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <CallDataModalTitle>{title}</CallDataModalTitle>
                            {spanishOnlyFilter && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: voicify_spark_darker,
                                    color: 'white',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    letterSpacing: '0.5px'
                                }}>ES</span>
                            )}
                        </div>
                        <CallDataDateRange>{moment(startDate).format('MMM D, YYYY')} - {moment(endDate).format('MMM D, YYYY')}</CallDataDateRange>
                    </CallDataModalTitleSection>
                    <CallDataModalCloseButton onClick={handleClose}>×</CallDataModalCloseButton>
                </CallDataModalHeader>

                <SimpleFilterControlsContainer>
                    <SimpleFilterRow>
                        <SearchInputWrapper>
                            <FilterSearchInput
                                type="text"
                                placeholder="Search all fields..."
                                value={filters.searchQuery}
                                onChange={(e) => filters.setSearchQuery(e.target.value)}
                            />
                            {filters.searchQuery && (
                                <ClearSearchButton onClick={() => filters.setSearchQuery('')}>×</ClearSearchButton>
                            )}
                        </SearchInputWrapper>
                        <ResultsInfo style={{ margin: 0 }}>
                            Showing <strong>{filters.filteredData.length}</strong>{filters.hasActiveFilters && !filters.isOnlyExcludingDefaultReasons && <> of <strong>{records.length}</strong></>} transfers
                            {filters.hasActiveFilters && (
                                <> {filters.isOnlyExcludingDefaultReasons ? '(excluding immediate transfers)' : '(filtered)'} <ClearFilterLink onClick={filters.clearAllFilters}>Clear all</ClearFilterLink></>
                            )}
                        </ResultsInfo>
                    </SimpleFilterRow>
                </SimpleFilterControlsContainer>

                <CallDataScrollContainer>
                    <CallDataTable>
                        <thead>
                            <tr>
                                {/* Date Column */}
                                <FilterableColumnHeader hasActiveFilter={filters.sortConfig?.key === 'date'}>
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Date</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'date'} onClick={() => filters.handleSort('date')} title="Sort">
                                            {filters.sortConfig?.key === 'date' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                    </ColumnHeaderContent>
                                </FilterableColumnHeader>

                                {/* Time Column */}
                                <FilterableColumnHeader hasActiveFilter={filters.sortConfig?.key === 'time'}>
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Time</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'time'} onClick={() => filters.handleSort('time')} title="Sort">
                                            {filters.sortConfig?.key === 'time' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                    </ColumnHeaderContent>
                                </FilterableColumnHeader>

                                {/* Phone Column */}
                                <FilterableColumnHeader
                                    hasActiveFilter={filters.selectedPhones.size > 0 || filters.sortConfig?.key === 'phone'}
                                    tabIndex={-1}
                                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                >
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Phone (Last 4)</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'phone'} onClick={() => filters.handleSort('phone')} title="Sort">
                                            {filters.sortConfig?.key === 'phone' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                        <ColumnFilterIcon
                                            isActive={filters.selectedPhones.size > 0}
                                            isOpen={filters.openColumnFilter === 'phone'}
                                            onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'phone' ? null : 'phone'); }}
                                            title="Filter"
                                        >
                                            <img src={filterIcon} alt="Filter" />
                                        </ColumnFilterIcon>
                                    </ColumnHeaderContent>
                                    {filters.openColumnFilter === 'phone' && (
                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                            <ColumnFilterHeader>
                                                <span>Filter Phone Numbers</span>
                                                <FilterActionsContainer>
                                                    <ClearFilterLink onClick={() => filters.setSelectedPhones(new Set(filters.uniquePhones))}>Select All</ClearFilterLink>
                                                    <ClearFilterLink onClick={() => filters.setSelectedPhones(new Set())}>Clear</ClearFilterLink>
                                                </FilterActionsContainer>
                                            </ColumnFilterHeader>
                                            <PhoneFilterSection>
                                                <PhoneFilterModeToggle>
                                                    <PhoneFilterModeButton isActive={filters.phoneFilterMode === 'include'} mode="include" onClick={() => filters.setPhoneFilterMode('include')}>Include</PhoneFilterModeButton>
                                                    <PhoneFilterModeButton isActive={filters.phoneFilterMode === 'exclude'} mode="exclude" onClick={() => filters.setPhoneFilterMode('exclude')}>Exclude</PhoneFilterModeButton>
                                                </PhoneFilterModeToggle>
                                                {filters.selectedPhones.size > 0 && (
                                                    <PhoneFilterList>
                                                        {[...filters.selectedPhones].map(phone => (
                                                            <PhoneFilterTag key={phone} isExclude={filters.phoneFilterMode === 'exclude'}>
                                                                ***{phone}
                                                                <PhoneFilterTagRemove onClick={() => filters.handlePhoneToggle(phone)}>×</PhoneFilterTagRemove>
                                                            </PhoneFilterTag>
                                                        ))}
                                                    </PhoneFilterList>
                                                )}
                                            </PhoneFilterSection>
                                            <ColumnFilterContent>
                                                {filters.uniquePhones.map(phone => (
                                                    <ColumnFilterItem key={phone} onClick={() => filters.handlePhoneToggle(phone)}>
                                                        <ColumnFilterCheckbox state={filters.selectedPhones.has(phone) ? 'checked' : 'unchecked'} />
                                                        <span>***{phone}</span>
                                                    </ColumnFilterItem>
                                                ))}
                                            </ColumnFilterContent>
                                        </ColumnFilterDropdown>
                                    )}
                                </FilterableColumnHeader>

                                {/* Location Column */}
                                <FilterableColumnHeader
                                    hasActiveFilter={filters.selectedLocations.size > 0 || filters.sortConfig?.key === 'location'}
                                    tabIndex={-1}
                                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                >
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Location</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'location'} onClick={() => filters.handleSort('location')} title="Sort">
                                            {filters.sortConfig?.key === 'location' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                        <ColumnFilterIcon
                                            isActive={filters.selectedLocations.size > 0}
                                            isOpen={filters.openColumnFilter === 'location'}
                                            onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'location' ? null : 'location'); }}
                                            title="Filter"
                                        >
                                            <img src={filterIcon} alt="Filter" />
                                        </ColumnFilterIcon>
                                    </ColumnHeaderContent>
                                    {filters.openColumnFilter === 'location' && (
                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                            <ColumnFilterHeader>
                                                <span>Filter by Location</span>
                                                <FilterActionsContainer>
                                                    <ClearFilterLink onClick={() => filters.setSelectedLocations(new Set(filters.uniqueLocations))}>Select All</ClearFilterLink>
                                                    <ClearFilterLink onClick={() => filters.setSelectedLocations(new Set())}>Clear</ClearFilterLink>
                                                </FilterActionsContainer>
                                            </ColumnFilterHeader>
                                            <ColumnFilterContent>
                                                {filters.uniqueLocations.map(location => (
                                                    <ColumnFilterItem key={location} onClick={() => {
                                                        const newSet = new Set(filters.selectedLocations);
                                                        if (newSet.has(location)) newSet.delete(location);
                                                        else newSet.add(location);
                                                        filters.setSelectedLocations(newSet);
                                                    }}>
                                                        <ColumnFilterCheckbox state={filters.selectedLocations.has(location) ? 'checked' : 'unchecked'} />
                                                        <span>{location}</span>
                                                    </ColumnFilterItem>
                                                ))}
                                            </ColumnFilterContent>
                                        </ColumnFilterDropdown>
                                    )}
                                </FilterableColumnHeader>

                                {/* Transcript Column */}
                                <CallDataTh>Transcript</CallDataTh>

                                {/* Transfer Type Column */}
                                <FilterableColumnHeader
                                    hasActiveFilter={filters.selectedTransferType !== 'all' || filters.sortConfig?.key === 'transferType'}
                                    tabIndex={-1}
                                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                >
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Transfer Type</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'transferType'} onClick={() => filters.handleSort('transferType')} title="Sort">
                                            {filters.sortConfig?.key === 'transferType' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                        <ColumnFilterIcon
                                            isActive={filters.selectedTransferType !== 'all'}
                                            isOpen={filters.openColumnFilter === 'transferType'}
                                            onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'transferType' ? null : 'transferType'); }}
                                            title="Filter"
                                        >
                                            <img src={filterIcon} alt="Filter" />
                                        </ColumnFilterIcon>
                                    </ColumnHeaderContent>
                                    {filters.openColumnFilter === 'transferType' && (
                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                            <ColumnFilterHeader>
                                                <span>Filter by Type</span>
                                                {filters.selectedTransferType !== 'all' && <ClearFilterLink onClick={() => filters.setSelectedTransferType('all')}>Clear</ClearFilterLink>}
                                            </ColumnFilterHeader>
                                            <ColumnFilterContent>
                                                <ColumnFilterItem onClick={() => filters.setSelectedTransferType('all')}><ColumnFilterCheckbox state={filters.selectedTransferType === 'all' ? 'checked' : 'unchecked'} /><span>All Types</span></ColumnFilterItem>
                                                <ColumnFilterItem onClick={() => filters.setSelectedTransferType('Planned')}><ColumnFilterCheckbox state={filters.selectedTransferType === 'Planned' ? 'checked' : 'unchecked'} /><span>Planned</span></ColumnFilterItem>
                                                <ColumnFilterItem onClick={() => filters.setSelectedTransferType('Unplanned')}><ColumnFilterCheckbox state={filters.selectedTransferType === 'Unplanned' ? 'checked' : 'unchecked'} /><span>Unplanned</span></ColumnFilterItem>
                                                <ColumnFilterItem onClick={() => filters.setSelectedTransferType('Undetermined')}><ColumnFilterCheckbox state={filters.selectedTransferType === 'Undetermined' ? 'checked' : 'unchecked'} /><span>Undetermined</span></ColumnFilterItem>
                                            </ColumnFilterContent>
                                        </ColumnFilterDropdown>
                                    )}
                                </FilterableColumnHeader>

                                {/* Transfer Reason Column */}
                                <FilterableColumnHeader
                                    hasActiveFilter={filters.selectedTransferReasons.size > 0 || filters.sortConfig?.key === 'transferReason'}
                                    tabIndex={-1}
                                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) filters.setOpenColumnFilter(null); }}
                                >
                                    <ColumnHeaderContent>
                                        <ColumnHeaderText>Transfer Reason</ColumnHeaderText>
                                        <SortIcon isActive={filters.sortConfig?.key === 'transferReason'} onClick={() => filters.handleSort('transferReason')} title="Sort">
                                            {filters.sortConfig?.key === 'transferReason' ? (filters.sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                                        </SortIcon>
                                        <ColumnFilterIcon
                                            isActive={filters.selectedTransferReasons.size > 0}
                                            isOpen={filters.openColumnFilter === 'transferReason'}
                                            onClick={(e) => { e.stopPropagation(); filters.setOpenColumnFilter(filters.openColumnFilter === 'transferReason' ? null : 'transferReason'); }}
                                            title="Filter"
                                        >
                                            <img src={filterIcon} alt="Filter" />
                                        </ColumnFilterIcon>
                                    </ColumnHeaderContent>
                                    {filters.openColumnFilter === 'transferReason' && (
                                        <ColumnFilterDropdown onClick={(e) => e.stopPropagation()}>
                                            <ColumnFilterHeader>
                                                <span>Filter by Reason</span>
                                                <FilterActionsContainer>
                                                    <ClearFilterLink onClick={() => filters.setSelectedTransferReasons(new Set(filters.uniqueReasons))}>Select All</ClearFilterLink>
                                                    <ClearFilterLink onClick={() => filters.setSelectedTransferReasons(new Set())}>Clear</ClearFilterLink>
                                                </FilterActionsContainer>
                                            </ColumnFilterHeader>
                                            <ColumnFilterContent>
                                                {filters.uniqueReasons.map(reason => (
                                                    <ColumnFilterItem key={reason} onClick={() => {
                                                        const newSet = new Set(filters.selectedTransferReasons);
                                                        if (newSet.has(reason)) newSet.delete(reason);
                                                        else newSet.add(reason);
                                                        filters.setSelectedTransferReasons(newSet);
                                                    }}>
                                                        <ColumnFilterCheckbox state={filters.selectedTransferReasons.has(reason) ? 'checked' : 'unchecked'} />
                                                        <span>{getReasonLabel(reason)}</span>
                                                    </ColumnFilterItem>
                                                ))}
                                            </ColumnFilterContent>
                                        </ColumnFilterDropdown>
                                    )}
                                </FilterableColumnHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {filters.filteredData.length === 0 ? (
                                <CallDataRow>
                                    <CallDataTd colSpan={7} style={{ textAlign: 'center', padding: '40px', color: color_text_dashboard_muted }}>
                                        No transfers match your current filters.
                                    </CallDataTd>
                                </CallDataRow>
                            ) : (
                                filters.paginatedData.map((transfer) => (
                                    <CallDataRow key={transfer.id}>
                                        <CallDataTd>{transfer.date}</CallDataTd>
                                        <CallDataTd>{transfer.time}</CallDataTd>
                                        <CallDataTd>***{transfer.phoneLastFour}</CallDataTd>
                                        <CallDataTd><TruncatedLocationText title={transfer.location}>{transfer.location}</TruncatedLocationText></CallDataTd>
                                        <CallDataTd>
                                            <TranscriptLink href={`/v/apps/${transfer.applicationId}/analytics/sessions?sessionId=${transfer.sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`} target="_blank" rel="noopener noreferrer">
                                                View Transcript
                                            </TranscriptLink>
                                        </CallDataTd>
                                        <CallDataTd>
                                            <CallTag style={{
                                                backgroundColor: transfer.transferType === 'Planned' ? chart_planned_fill_light : transfer.transferType === 'Unplanned' ? chart_unplanned_fill_light : voicify_spark_light,
                                                color: transfer.transferType === 'Planned' ? chart_planned_solid : transfer.transferType === 'Unplanned' ? chart_unplanned_solid : voicify_spark_dark
                                            }}>
                                                {transfer.transferType}
                                            </CallTag>
                                        </CallDataTd>
                                        <CallDataTd>{getReasonLabel(transfer.transferReason) || transfer.transferReason}</CallDataTd>
                                    </CallDataRow>
                                ))
                            )}
                        </tbody>
                    </CallDataTable>
                </CallDataScrollContainer>

                <CallDataModalFooter>
                    {filters.totalPages > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
                            <button
                                onClick={() => filters.setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={filters.currentPage === 0}
                                style={{ padding: '4px 8px', cursor: filters.currentPage === 0 ? 'not-allowed' : 'pointer', opacity: filters.currentPage === 0 ? 0.5 : 1 }}
                            >
                                ← Prev
                            </button>
                            <span style={{ fontSize: '13px', color: color_text_dashboard_muted }}>
                                Page {filters.currentPage + 1} of {filters.totalPages}
                            </span>
                            <button
                                onClick={() => filters.setCurrentPage(p => Math.min(filters.totalPages - 1, p + 1))}
                                disabled={filters.currentPage >= filters.totalPages - 1}
                                style={{ padding: '4px 8px', cursor: filters.currentPage >= filters.totalPages - 1 ? 'not-allowed' : 'pointer', opacity: filters.currentPage >= filters.totalPages - 1 ? 0.5 : 1 }}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                    <ExportInfoText>
                        {filters.hasActiveFilters
                            ? `Exporting ${filters.filteredData.length} filtered results`
                            : `Exporting all ${records.length} results`}
                    </ExportInfoText>
                    <ExportButton onClick={handleExportCSV} disabled={isExporting || records.length === 0}>
                        {isExporting && <ButtonSpinner />}
                        {isExporting ? 'Exporting...' : 'Export to CSV'}
                    </ExportButton>
                </CallDataModalFooter>
            </CallDataModalContent>
        </ModalLayout>
    );
};

export default TransferDataModal;
